import { Location } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnDestroy } from '@angular/core';
import { BehaviorSubject, map, shareReplay, Subject, takeUntil } from 'rxjs';
import { randomId } from 'server/shared/helpers/random';
import { DemandReason } from 'server/shared/models/demandReason';
import { CustomerService } from '../../services/customer.service';
import { PermissionsService } from '../../services/permissions.service';
import { ToastService } from '../../services/toast/toast.service';
@Component({
  selector: 'admin-demand-reasons',
  templateUrl: 'demand-reasons.html',
  styleUrls: ['demand-reasons.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DemandReasonsComponent implements OnDestroy {
  customer$ = this.customerService.readyDispoCustomer$;

  private destroyed$ = new Subject<void>();

  demandReasons$ = this.customer$.pipe(
    map((customer) =>
      JSON.parse(JSON.stringify(customer?.demandReasons || [])),
    ),
    shareReplay(1),
  );

  demandReasonsInCreation$ = new BehaviorSubject<
    {
      displayName: string;
      enableDescription?: boolean;
    }[]
  >([]);

  constructor(
    private toastService: ToastService,
    private customerService: CustomerService,
    private permissionService: PermissionsService,
    private location: Location,
  ) {
    this.permissionService.isCustomerAdmin$
      .pipe(takeUntil(this.destroyed$))
      .subscribe((isCustomerAdmin) => {
        if (!isCustomerAdmin) {
          this.historyBack();
        }
      });
  }

  ngOnDestroy() {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  historyBack() {
    this.location.back();
  }

  addNewDemandReason() {
    const current = this.demandReasonsInCreation$.value;
    this.demandReasonsInCreation$.next([...current, { displayName: '' }]);
  }

  cancelNewDemandReason(index: number) {
    const current = this.demandReasonsInCreation$.value;
    const updated = [...current];
    updated.splice(index, 1);
    this.demandReasonsInCreation$.next(updated);
  }

  async saveNewDemandReason(index: number) {
    const demandReasonsInCreation = this.demandReasonsInCreation$.value;
    let displayName = demandReasonsInCreation[index]?.displayName;
    let enableDescription = demandReasonsInCreation[index]?.enableDescription;
    const customer = await this.customerService.readyDispoCustomer;
    if (!displayName || !customer) return;

    let newDemandReason: DemandReason = {
      id: randomId(),
      displayName: displayName,
      enableDescription: enableDescription,
    };

    // Remove from creation list
    const updatedInCreation = [...demandReasonsInCreation];
    updatedInCreation.splice(index, 1);
    this.demandReasonsInCreation$.next(updatedInCreation);

    // The server call will update the customer data, which will automatically update demandReasons$
    Meteor.call(
      'addDemandReason',
      customer._id,
      newDemandReason,
      (error: Meteor.Error) => {
        if (error) {
          this.toastService.add({
            type: 'ERROR',
            message: error.error?.toString(),
          });
        } else {
          this.toastService.add({ type: 'SUCCESS', message: 'changesSaved' });
        }
      },
    );
  }

  async deleteDemandReason(demandReasonId: string) {
    const customer = await this.customerService.readyDispoCustomer;
    if (!customer || !demandReasonId) return;

    // The server call will update the customer data, which will automatically update demandReasons$
    Meteor.call(
      'removeDemandReason',
      customer._id,
      demandReasonId,
      (error: Meteor.Error) => {
        if (error) {
          this.toastService.add({
            type: 'ERROR',
            message: error.error?.toString(),
          });
        } else {
          this.toastService.add({ type: 'SUCCESS', message: 'changesSaved' });
        }
      },
    );
  }

  cancelChanges(demandReasonId: string) {
    // Since demandReasons$ is now derived from customer data,
    // the cancel functionality would need to be handled differently.
    // For now, this could trigger a refresh of the data or
    // we could implement a local editing state system.
    // This is a design decision that depends on the UX requirements.
  }

  async saveDemandReason(demandReason: DemandReason): Promise<void> {
    const customer = await this.customerService.readyDispoCustomer;
    if (!customer || !demandReason) return;
    Meteor.call(
      'updateDemandReason',
      customer._id,
      demandReason,
      (error: Meteor.Error) => {
        if (error) {
          this.toastService.add({
            type: 'ERROR',
            message: error.error?.toString(),
          });
        } else {
          this.toastService.add({ type: 'SUCCESS', message: 'changesSaved' });
        }
      },
    );
  }

  trackById(index: number, object: DemandReason): string {
    return object.id;
  }
}
